import { Injector, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { BehaviorSubject, filter, map, Observable, ReplaySubject, share, Subject, tap, timer } from "rxjs";

/**
 * libs
 */
import { 
    ClientSubscribeCallback,
    ErrorWithReasonCode,
    MqttClient,
    Packet
} from "mqtt";

/**
 * interfaces
 */
import { ITopicHandlerResult, ITopicOptions, TopicFilter, TopicHandlerStreamState } from '../../interfaces';

/**
 * Manages the lifecycle and data stream of a single MQTT topic subscription.
 * @class TopicHandler
 * @template TPayload - The type of the data payload expected from this topic.
 * @description
 * - Subscribes to the topic upon creation.
 * - Exposes the data as an RxJS Observable (`observeTopic`).
 * - Exposes the data as an Angular Signal (`topicSignal`).
 * - Handles the internal state of the subscription (subscribed, error, idle).
 * - Implements `shareReplay` to ensure late subscribers get the latest data.
 */
export class TopicHandler<TPayload = any> {

    /**
     * Subject to notify when the handler shuts down (refCount zero timeout).
     * @private
     */
    private readonly _shutdown$ = new Subject<void>();

    /**
     * Observable that emits when the handler shuts down.
     * @public
     */
    public readonly shutdown$ = this._shutdown$.asObservable();

    /**
     * Internal subject to hold the full result (payload + metadata).
     * @private
     */
    // 1. Remove initializer here
    private _streamSubject$!: BehaviorSubject<ITopicHandlerResult<TPayload> | null>;

    /**
     * Stream of full topic results (including packet metadata).
     * @public
     */
    // 2. Remove initializer here
    public readonly stream$!: Observable<ITopicHandlerResult<TPayload>>;

    /**
     * Gets the current value of the stream synchronously.
     * @public
     * @returns {ITopicHandlerResult<TPayload> | null} The current result or null.
     */
    public stream(): ITopicHandlerResult<TPayload> | null {
        return this._streamSubject$.getValue();
    }

    /**
     * Internal subject to track the subscription state.
     * @private
     */
    private readonly _streamStateSubject$: BehaviorSubject<TopicHandlerStreamState> = new BehaviorSubject<TopicHandlerStreamState>('initializing');
    
    /**
     * Stream of subscription states (e.g., 'subscribed', 'error').
     * @public
     */
    public streamState$: Observable<TopicHandlerStreamState> = this._streamStateSubject$.asObservable();

    /**
     * Gets the current subscription state synchronously.
     * @public
     */
    public get streamState(): TopicHandlerStreamState {
        return this._streamStateSubject$.getValue();
    }

    /**
     * Clean Observable that emits ONLY the payload data.
     * Filters for 'publish' packets.
     * Use this for most business logic.
     * @public
     */
    public readonly observeTopic!: Observable<TPayload>;

    /**
     * Creates an instance of TopicHandler.
     * @constructor
     * @param {MqttClient} client - The underlying MQTT client.
     * @param {TopicFilter} topic - The topic string or filter.
     * @param {Partial<ITopicOptions<TPayload>>} [options] - Subscription options (QoS, etc.).
     */
    constructor(
        private readonly client: MqttClient,
        public readonly topic: TopicFilter,
        private readonly options?: Partial<ITopicOptions<TPayload>>,
    ) {
        
        // global validation
        if (!this.client || Object.hasOwn(this.client, 'connect')) {
            throw new Error('Client must be an instance of MqttClient');
        }
        if (typeof this.topic !== 'string' && typeof this.topic !== 'object') {
            throw new Error('Topic must be a string or object');
        }

        // merge the default options with the provided options
        this.options = { ...TopicHandler.defaultOptions, ...this.options };

        // get the initial payload
        const { initialValue, resetOnRefCountZero } = this.options;
        
        // create the stream subject
        this._streamSubject$ = new BehaviorSubject<ITopicHandlerResult<TPayload> | null>({ payload: initialValue });

        // create the stream observable
        this.stream$ = new Observable<ITopicHandlerResult<TPayload>>((observer) => {
            
            // Subscribe to the MQTT broker (if not already)
            this.subscribe();

            // Connect the observer to the internal data subject
            const subscription = this._streamSubject$
                .pipe(filter((result) => result !== null))
                .subscribe(observer);

            // Teardown logic
            return () => {
                subscription.unsubscribe();
                this.unsubscribe();
                this._shutdown$.next();
            };

        }).pipe(
            share({
                // Use ReplaySubject(1) so new subscribers get the latest value immediately
                connector: () => new ReplaySubject(1),
                
                // use the dynamic delay from options
                resetOnRefCountZero: () => timer(resetOnRefCountZero ?? TopicHandler.defaultOptions.resetOnRefCountZero),
                
                resetOnComplete: true,
                resetOnError: true
            })
        );

      // create the observe topic observable
      this.observeTopic = this.stream$.pipe(
        map(result => result?.payload),
        filter(payload => payload != null )
    );
        
    }

    /**
     * Subscribes to the topic.
     * Idempotent: does nothing if already subscribed.
     * @public
     */
    public subscribe(): void {
        
        // If the topic is already subscribed, do nothing
        if (this.streamState === 'subscribed' || this.streamState === 'idle') {
            console.info('Topic already subscribed or idle, skipping subscription');
            return;
        }

        // the stream state is in idle state
        this._streamStateSubject$.next('idle');

        const callback: ClientSubscribeCallback = ((err, granted, packet) => {
            if (err) {
                this._streamStateSubject$.next('error');
                this._streamSubject$.next({ err, granted, packet });
            }
            else {
                this._streamStateSubject$.next('subscribed');
                this._streamSubject$.next({ granted, packet });
            }
        });
        
        this.client.subscribe(this.topic, this.options, callback);
    }

    /**
     * Unsubscribes from the topic.
     * @public
     */
    public unsubscribe(): void {

        // If the topic is already unsubscribed, do nothing
        if (this.streamState === 'unsubscribed' || this.streamState === 'idle') {
            console.info('Topic already unsubscribed or initializing, skipping unsubscribe');
            return;
        }
        
        // the stream state is in idle state
        this._streamStateSubject$.next('idle');

        const topic = this.topic as string | string[];

        this.client.unsubscribe(topic, this.options, (error?: Error | ErrorWithReasonCode, packet?: Packet) => {
            if (error) {
                this._streamStateSubject$.next('error');
                this._streamSubject$.next({ err: error, packet, payload: null as TPayload });
            }
            else {
                this._streamStateSubject$.next('unsubscribed');
                this._streamSubject$.next({ packet, payload: null as TPayload });
            }
        });
    }

    /**
     * Pushes a new payload into the stream.
     * Typically called by the ConnectionHandler when a message arrives.
     * @public
     * @param {TPayload} payload - The data received.
     * @param {Packet} [packet] - The raw MQTT packet.
     */
    public setPayload(payload: TPayload, packet?: Packet): void {
        this._streamSubject$.next({ payload, packet });
    }

    /**
     * Converts the topic stream into an Angular Signal.
     * @public
     * @param {TPayload} [initialValue] - Optional initial value.
     * @param {Injector} [injector] - Injector owning the signal's cleanup. Pass
     * one when calling from outside an injection context — an HTTP callback, a
     * `tap`, a subscribe handler — where `toSignal` cannot reach a `DestroyRef`
     * on its own.
     * @returns {Signal<TPayload>} A signal that updates with new topic data.
     * @description
     * The signal unsubscribes when its owning context is destroyed, which drops
     * this handler's refCount and lets the `resetOnRefCountZero` teardown
     * unsubscribe from the broker and clear the registry entry.
     */
    public topicSignal(initialValue: TPayload = null as TPayload, injector?: Injector): Signal<TPayload> {
        return toSignal(this.observeTopic, { initialValue, injector });
    }

    /**
     * Destroys the handler, unsubscribes, and completes streams.
     * @public
     * @description
     * Emits `shutdown$` first so `TopicRegistry` drops this handler from its
     * map. Without that, a destroyed handler stays registered and
     * `createOrGetTopic` hands back an instance whose subjects are already
     * completed.
     */
    public destroy(): void {
        this.unsubscribe();
        this._shutdown$.next();
        this._streamSubject$.complete();
        this._streamStateSubject$.complete();
    }

    /**
     * The default options for the topic handler.
     * @public
     * @static
     * @returns {Partial<ITopicOptions<any>>} The default options.
     */
    public static defaultOptions: ITopicOptions<any> = {
        qos: 0,
        initialValue: null,
        resetOnRefCountZero: 20000,
    };

}
